/**
 * visitProviderCategories — single source of truth for the Manage Visits
 * "provider type" filter (All / OPD / OTC / Lab & Diagnostic, extensible).
 *
 * A visit's provider is derived from its backend `visit_type` (the VisitType
 * enum). Doctor-led types (REGULAR / EMERGENCY / FOLLOW_UP / REPORT_CHECKUP /
 * APPOINTMENT) all roll up to the **OPD** bucket, which is also the fallback for
 * any future doctor-led type. NURSE and LAB are their own buckets.
 *
 * Distinct from `components/visit/providerCapabilities.js`, which is the
 * registration FORM's provider toggle (DOCTOR / NURSE / LAB). Here OPD is the
 * doctor bucket because it spans several visit_types. Add a future provider type
 * once in PROVIDER_CATEGORIES (frontend) + PROVIDER_VISIT_TYPES (backend) + the
 * VisitType enum, and every chip / badge / roster / list filter / payment scope
 * picks it up.
 */
import { STAFF_ROLES } from './staff';

export const PROVIDER_CATEGORIES = [
  {
    key: 'OPD',
    label: 'OPD',
    icon: '🩺',
    visitTypes: ['REGULAR', 'EMERGENCY', 'FOLLOW_UP', 'REPORT_CHECKUP', 'APPOINTMENT'],
    staffRole: STAFF_ROLES.DOCTOR,
    assigneeField: 'doctor_member_id',
    // OPD payments are attributed per-doctor in the ledger, so a selected doctor
    // narrows the payment cards. Nurse/Lab have no per-assignee attribution today.
    paymentByAssignee: true,
  },
  {
    key: 'NURSE',
    // Full name is the ratified surface label; `shortLabel` is the
    // space-constrained form (chips, toggle, section titles, badges) — both
    // single-sourced here so no surface re-hardcodes "Nurse Visit". The
    // `NURSE` key/visitTypes stay unchanged (display-only rename).
    label: 'Outpatient Treatment Center (OTC)',
    shortLabel: 'OTC',
    icon: '⚕',
    visitTypes: ['NURSE'],
    staffRole: STAFF_ROLES.NURSE,
    assigneeField: 'nurse_member_id',
    // OTC visits are attended by EXACTLY ONE of a nurse OR a doctor (W3 XOR).
    // The assignee filter therefore lists BOTH roles so a doctor-attended OTC
    // visit is filterable by its attending doctor (matched via that doctor's
    // `doctor_member_id` in `visitMatchesAssignee`, which scans every field).
    // `staffRole` stays NURSE (the default/primary attendee); `assigneeRoles`
    // is the full set consumers iterate when it differs from the single role.
    assigneeRoles: [STAFF_ROLES.NURSE, STAFF_ROLES.DOCTOR],
    paymentByAssignee: false,
  },
  {
    key: 'LAB',
    label: 'Lab & Diagnostic',
    icon: '🧪',
    visitTypes: ['LAB'],
    staffRole: STAFF_ROLES.LAB_TECHNICIAN,
    assigneeField: 'lab_technician_member_id',
    paymentByAssignee: false,
  },
];

/** Catch-all bucket for doctor-led / unrecognised visit_types. */
export const DEFAULT_PROVIDER_KEY = 'OPD';

const CATEGORY_BY_KEY = PROVIDER_CATEGORIES.reduce((m, c) => { m[c.key] = c; return m; }, {});

const KEY_BY_VISIT_TYPE = PROVIDER_CATEGORIES.reduce((m, c) => {
  for (const vt of c.visitTypes) m[vt] = c.key;
  return m;
}, {});

/** Provider-category key for a visit; OPD is the fallback. */
export function providerCategoryOf(visit) {
  const vt = (visit?.visit_type || '').toUpperCase();
  return KEY_BY_VISIT_TYPE[vt] || DEFAULT_PROVIDER_KEY;
}

/** The visit_types a provider key owns; [] for '' (All) / unknown. */
export function visitTypesForProvider(providerKey) {
  return CATEGORY_BY_KEY[providerKey]?.visitTypes || [];
}

/** Look up a category config by key (undefined for '' / unknown). */
export function providerCategory(providerKey) {
  return CATEGORY_BY_KEY[providerKey];
}

/**
 * True when a visit is handled by the given assignee member_id, checking every
 * provider's assignee field — so a combined (cross-role) "All" person filter
 * matches regardless of whether the person is a doctor, nurse, or lab tech.
 * An empty id means "no person filter" → matches everything.
 */
export function visitMatchesAssignee(visit, memberId) {
  if (!memberId) return true;
  return PROVIDER_CATEGORIES.some((c) => visit?.[c.assigneeField] === memberId);
}

/**
 * Badge colors per visit_type — single source for ManageVisitPage's type badge.
 * NURSE / LAB were previously missing, which fell back to the REGULAR grey.
 */
export const VISIT_TYPE_COLORS = {
  REGULAR:        { bg: 'rgba(107,114,128,0.12)', text: '#6b7280' },
  EMERGENCY:      { bg: 'rgba(239,68,68,0.12)', text: '#ef4444' },
  FOLLOW_UP:      { bg: 'rgba(20,184,166,0.12)', text: '#14b8a6' },
  REPORT_CHECKUP: { bg: 'rgba(13, 148, 136,0.12)', text: '#0d9488' },
  APPOINTMENT:    { bg: 'rgba(59,130,246,0.12)', text: '#3b82f6' },
  NURSE:          { bg: 'rgba(20,184,166,0.12)', text: '#0d9488' },
  LAB:            { bg: 'rgba(139,92,246,0.12)', text: '#7c3aed' },
};

/**
 * Badge display text per visit_type — single source for ManageVisitPage's
 * type badge label. NURSE and LAB override their raw stored value with the
 * OTC / LAB & DIAG short forms (OR-43); every other visit_type falls back to
 * the raw value.
 */
export const VISIT_TYPE_LABELS = {
  NURSE: 'OTC',
  LAB: 'LAB & DIAG',
};
